/**
 * Container for platform-specific PositionMultiSelect.
 * On native, closes the dropdown after each selection (SE-3983) so it doesn't
 * block the rest of the form — the user can reopen to add more positions.
 * Metro resolves the View to platform-specific implementations:
 * - Web: react-select with isMulti for optimal dropdown UX
 * - Native: react-native-element-dropdown MultiSelect
 * @module components/ManuallyAddPlayerModal/PositionMultiSelect
 */

import { useCallback, useRef } from "react";

import { PositionMultiSelectProps } from "@/types/manuallyAddPlayerModalTypes";

import PositionMultiSelectView from "./PositionMultiSelectView";

/**
 * Wraps onChange to close the native dropdown after each selection
 * @param props - Position multi-select props from parent
 * @param props.onChange - Selection change callback, wrapped to also close dropdown
 */
const PositionMultiSelectContainer = ({
  onChange,
  ...rest
}: PositionMultiSelectProps) => {
  const multiSelectRef = useRef<{ open: () => void; close: () => void }>(null);

  /** Closes the dropdown after each selection so it doesn't block the form */
  const handleChange = useCallback(
    (selected: string[]) => {
      onChange(selected);
      multiSelectRef.current?.close();
    },
    [onChange]
  );

  /** Clears all selected positions at once */
  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <PositionMultiSelectView
      {...rest}
      onChange={handleChange}
      onClearAll={handleClearAll}
      multiSelectRef={multiSelectRef}
    />
  );
};

export default PositionMultiSelectContainer;
